import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const lightTheme = {
  name: 'light',
  colors: {
    // Backgrounds
    background: '#f8f9fa',
    surface: '#ffffff',
    surfaceAlt: '#f8f9fa',
    
    // Text
    text: '#333333',
    textSecondary: '#666666',
    textTertiary: '#999999',
    
    // Brand colors
    primary: '#8B5A96',
    primaryDark: '#6B2E7A',
    primaryLight: '#A569BD',
    
    // Accent colors
    accent: '#FF6B6B',
    accentDark: '#E85A5A',
    
    // UI elements
    border: '#dddddd',
    shadow: 'rgba(0,0,0,0.1)',
    shadowHover: 'rgba(139, 90, 150, 0.2)',
    
    // Status colors
    success: '#28a745',
    successBg: '#d4edda',
    successText: '#155724',
    warning: '#FFC107',
    warningBg: '#fff3cd',
    warningText: '#856404',
    danger: '#dc3545',
    dangerBg: '#f8d7da',
    dangerText: '#721c24',
    info: '#17a2b8',
    infoBg: '#e3f2fd',
    infoText: '#1565c0',
    
    // Interactive
    hover: '#8B5A96',
    hoverText: '#ffffff',
  }
};

export const darkTheme = {
  name: 'dark',
  colors: {
    // Backgrounds (matching screenshot - dark navy/blue-black)
    background: '#0f1419',
    surface: '#1a2332',
    surfaceAlt: '#141d29',
    
    // Text
    text: '#e0e6ed',
    textSecondary: '#a0aab8',
    textTertiary: '#6b7280',
    
    // Brand colors (lighter purple for dark mode)
    primary: '#a78bfa',
    primaryDark: '#8b5cf6',
    primaryLight: '#c4b5fd',
    
    // Accent colors
    accent: '#FF8E53',
    accentDark: '#FF6B6B',
    
    // UI elements
    border: '#2d3748',
    shadow: 'rgba(0,0,0,0.4)',
    shadowHover: 'rgba(167, 139, 250, 0.3)',
    
    // Status colors
    success: '#4caf50',
    successBg: '#1a2e1f',
    successText: '#81c784',
    warning: '#FFD54F',
    warningBg: '#2d2515',
    warningText: '#FFD54F',
    danger: '#f44336',
    dangerBg: '#2d1a1a',
    dangerText: '#ef5350',
    info: '#29b6f6',
    infoBg: '#1a2530',
    infoText: '#64b5f6',
    
    // Interactive
    hover: '#a78bfa',
    hoverText: '#ffffff',
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'light' ? lightTheme : darkTheme;
  });

  useEffect(() => {
    localStorage.setItem('theme', theme.name);
    document.body.style.backgroundColor = theme.colors.background;
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme.name === 'light' ? darkTheme : lightTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
